import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { formatImageUrl } from '../../utils/imageUtils';

export default function ProductCard({ product }) {
  const { user } = useAuth();
  const { addToCart, toggleWishlist, isInWishlist } = useCart();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [quickMsg, setQuickMsg] = useState('');

  // Helper function for Rupee Formatting
  const formatINR = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (product.stock === 0) return;
    setAdding(true);
    try {
      await addToCart(product._id, 1);
      setQuickMsg('Added');
      setTimeout(() => setQuickMsg(''), 1500);
    } catch { }
    setAdding(false);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    await toggleWishlist(product._id);
  };

  const discountPct = product.offerPercentage > 0 ? product.offerPercentage : 0;
  const inWishlist = isInWishlist(product._id);

  // Seller display name
  const seller = product.createdBy;
  const sellerName = seller?.storeName || (seller ? `${seller.firstName} ${seller.lastName}` : null);
  const sellerId = seller?._id;

  return (
    <div className="product-card group">
      <Link to={`/product/${product._id}`}>
        {/* Image container */}
        <div className="relative overflow-hidden bg-[#EFEDE8] aspect-[3/4]">
          <img
            src={formatImageUrl(product.images?.[0]) || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80'}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80'; }} 
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.priceType === 'affordable' && (
              <span className="bg-emerald-600 text-white text-[9px] px-2 py-0.5 font-sans tracking-widest uppercase">
                Affordable
              </span>
            )}
            {discountPct > 0 && (
              <span className="bg-lux-gold text-white text-[9px] px-2 py-0.5 font-sans tracking-widest uppercase">
                {discountPct}% OFF
              </span>
            )}
            {product.stock === 0 && (
              <span className="bg-black text-white text-[9px] px-2 py-0.5 font-sans tracking-widest uppercase">
                Sold Out
              </span>
            )}
          </div>

          {/* Wishlist button */}
          <button
            onClick={handleWishlist}
            className={`absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 backdrop-blur-sm transition-all duration-200 hover:bg-white ${inWishlist ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill={inWishlist ? "#C6A75E" : "none"} stroke={inWishlist ? "#C6A75E" : "#111"} strokeWidth="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>

          {/* Quick add */}
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button
              onClick={handleAddToCart}
              disabled={adding || product.stock === 0}
              className="w-full bg-lux-primary text-white text-[10px] py-3 tracking-[0.2em] uppercase font-sans font-medium hover:bg-lux-gold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {quickMsg || (product.stock === 0 ? 'Out of Stock' : (adding ? 'Adding...' : 'Quick Add'))}
            </button>
          </div>
        </div>

        {/* Product Info */}
        <div className="pt-5 pb-2">
          <p className="text-[10px] text-gray-400 font-sans tracking-[0.2em] uppercase mb-1.5">{product.brand || 'AURÉNE'}</p>
          <h3 className="font-display text-lux-primary font-light text-lg tracking-tight mb-2 truncate group-hover:text-lux-gold transition-colors">{product.name}</h3>

          <div className="flex items-center gap-3">
            {discountPct > 0 ? (
              <>
                <span className="font-sans text-sm font-semibold text-lux-primary">
                  {formatINR(product.finalPrice)}
                </span>
                <span className="font-sans text-[11px] text-gray-300 line-through">
                  {formatINR(product.price)}
                </span>
              </>
            ) : (
              <span className="font-sans text-sm font-semibold text-lux-primary">
                {formatINR(product.price)}
              </span>
            )}
          </div>

          {/* Rating */}
          {product.numReviews > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <svg key={s} width="11" height="11" viewBox="0 0 24 24"
                    fill={s <= Math.round(product.averageRating) ? '#C6A75E' : 'none'}
                    stroke={s <= Math.round(product.averageRating) ? '#C6A75E' : '#D1CBC0'}
                    strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                ))}
              </div>
              <span className="text-[10px] font-sans text-lux-secondary">({product.numReviews})</span>
            </div>
          )}
        </div>
      </Link>

      {/* Seller name */}
      {sellerName && sellerId && (
        <Link
          to={`/seller/${sellerId}`}
          onClick={e => e.stopPropagation()}
          className="block text-[10px] text-gray-400 hover:text-lux-gold font-sans tracking-wider transition-colors mt-0.5"
        >
          Seller: {sellerName}
        </Link>
      )}
    </div>
  );
}